import { createElement, useState, type ReactNode } from 'react'
import { CustomWorkflowRunModal } from './CustomWorkflowRunModal'
import { useCustomWorkflowsEnabled } from '../../common/useCustomWorkflowsEnabled'
import { CustomWorkflowContext, CustomWorkflowSummary, useListCustomWorkflowsQuery } from '../../store/customWorkflow/customWorkflows'

export interface UseCustomWorkflowLauncherOptions {
  accountId: string
  scope: string
  context?: CustomWorkflowContext
  contextPreviews?: Record<string, ReactNode>
  title?: string
}

export interface CustomWorkflowLauncher {
  enabled: boolean
  workflows: CustomWorkflowSummary[]
  isLoading: boolean
  launch: (workflowId?: string, contextOverride?: CustomWorkflowContext) => void
  runModal: ReactNode
}

export const useCustomWorkflowLauncher = ({ accountId, scope, context, contextPreviews, title }: UseCustomWorkflowLauncherOptions): CustomWorkflowLauncher => {
  const { enabled } = useCustomWorkflowsEnabled()
  const { data: workflows, isLoading } = useListCustomWorkflowsQuery({ accountId, scope, triggerType: 'manual' }, { skip: !accountId || !enabled })
  const [launched, setLaunched] = useState<{ workflowId?: string; context?: CustomWorkflowContext } | null>(null)

  const contextKey = JSON.stringify(context ?? {})
  const [prevContextKey, setPrevContextKey] = useState(contextKey)
  if (prevContextKey !== contextKey) {
    setPrevContextKey(contextKey)
    setLaunched(null)
  }

  const runModal =
    launched && enabled
      ? createElement(CustomWorkflowRunModal, {
          show: true,
          onHide: () => setLaunched(null),
          scope,
          workflowId: launched.workflowId,
          context: launched.context ?? context,
          contextPreviews,
          title,
        })
      : null

  return {
    enabled,
    workflows: workflows ?? [],
    isLoading,
    launch: (workflowId?: string, contextOverride?: CustomWorkflowContext) => setLaunched({ workflowId, context: contextOverride }),
    runModal,
  }
}
